import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Professor,
  CreateProfessorDto,
  UpdateProfessorDto,
} from '../../../../core/interfaces/professor';
import { Subject } from '../../../../core/interfaces/subject';
import { LearningService } from '../../../../core/services/learning-path.service';
import { ProfessorService } from '../../../../core/services/professor.service';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import {
  CustomSelectComponent,
  SelectOption,
} from '../../../../components/ui/custom-select/custom-select.component';
import { UploadService, UploadPurpose } from '../../../../core/services/uploads/upload.service';
import { finalize } from 'rxjs';

/** Une affectation retenue : un couple parcours·matière. */
interface AssignmentItem {
  pathId: number;
  pathTitle: string;
  subjectId: number;
  subjectName: string;
}

@Component({
  selector: 'app-add-professor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CustomSelectComponent,
  ],
  templateUrl: './add-professor.component.html',
  styleUrl: './add-professor.component.css',
})
export class AddProfessorComponent implements OnInit, OnChanges {
  @Input() professor: Professor | null = null;
  @Input() isEditMode = false;
  /** Défaut de matière (venant du filtre) ; l'admin peut le changer ici. */
  @Input() subjectId: number | null = null;
  /** Défaut de parcours (venant du filtre). */
  @Input() learningPathId: number | null = null;
  /** Parcours disponibles pour le sélecteur d'affectation du modal. */
  @Input() learningPaths: LearningPath[] = [];

  @Output() saved = new EventEmitter<CreateProfessorDto | UpdateProfessorDto>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private learning = inject(LearningService);
  private professorService = inject(ProfessorService);
  private uploadService = inject(UploadService);

  professorForm!: FormGroup;

  /** Preview URL for the professor image. */
  imagePreviewUrl: string | null = null;
  /** Upload key returned by the API after image upload. */
  imageKey: string | null = null;
  /** True while the image is being uploaded. */
  isUploadingImage = false;

  /** Matières du parcours choisi dans le sélecteur (cascade). */
  subjects: Subject[] = [];
  /** Affectations retenues (un prof peut enseigner plusieurs matières/niveaux). */
  assignments: AssignmentItem[] = [];
  /** Vrai si l'on tente d'enregistrer sans aucune affectation. */
  assignmentError = false;
  /** Vrai pendant le chargement des affectations existantes (mode édition). */
  isLoadingAssignments = false;
  /** Vrai si ce chargement a échoué : l'enregistrement est alors bloqué. */
  assignmentsLoadFailed = false;
  /** Matière à ajouter automatiquement une fois ses libellés chargés. */
  private pendingDefaultSubjectId: number | null = null;

  /**
   * Options des deux sélecteurs recherchables (cascade parcours → matière).
   * Matérialisées et non calculées à la volée : un accesseur renverrait un
   * nouveau tableau à chaque cycle de détection, et le sélecteur, qui se
   * resynchronise quand `options` change, effacerait la recherche en cours.
   */
  pathOptions: SelectOption[] = [];
  subjectOptions: SelectOption[] = [];

  private rebuildPathOptions(): void {
    this.pathOptions = this.learningPaths.map((p) => ({
      id: p.id,
      title: p.title,
    }));
  }

  private rebuildSubjectOptions(): void {
    this.subjectOptions = this.subjects.map((s) => ({
      id: s.id,
      title: s.name,
    }));
  }

  ngOnInit(): void {
    this.initializeForm();
    this.rebuildPathOptions();
    this.populateFormIfEdit();
    this.resetAssignmentState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['learningPaths']) this.rebuildPathOptions();

    // Le modal est réutilisé (dans app-modal) : à chaque réouverture / bascule
    // édition, on réaligne le formulaire et l'état des affectations.
    if (
      this.professorForm &&
      (changes['professor'] ||
        changes['isEditMode'] ||
        changes['subjectId'] ||
        changes['learningPathId'])
    ) {
      this.populateFormIfEdit();
      this.resetAssignmentState();
    }
  }

  private initializeForm(): void {
    this.professorForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      grade: ['', [Validators.required]],
      specialization: ['', [Validators.required, Validators.minLength(2)]],
      // Sélecteurs d'affectation (non requis : on valide sur la liste finale).
      learningPathId: [this.learningPathId],
      subjectId: [null],
      isActive: [true],
    });
  }

  private populateFormIfEdit(): void {
    if (this.isEditMode && this.professor) {
      this.professorForm.patchValue({
        firstName: this.professor.firstName,
        lastName: this.professor.lastName,
        email: this.professor.email,
        grade: this.professor.grade,
        specialization: this.professor.specialization,
        isActive: this.professor.isActive,
      });
      this.imageKey = this.professor.imageKey ?? null;
      this.imagePreviewUrl = this.professor.imageUrl ?? null;
    } else if (!this.isEditMode) {
      this.professorForm.reset({
        firstName: '',
        lastName: '',
        email: '',
        grade: '',
        specialization: '',
        learningPathId: this.learningPathId,
        subjectId: null,
        isActive: true,
      });
      this.imageKey = null;
      this.imagePreviewUrl = null;
    }
  }

  /** Réinitialise les affectations et pré-charge la matière du filtre. */
  private resetAssignmentState(): void {
    this.assignments = [];
    this.assignmentError = false;
    this.assignmentsLoadFailed = false;
    this.subjects = [];
    this.rebuildSubjectOptions();
    this.professorForm.patchValue({
      learningPathId: this.learningPathId,
      subjectId: null,
    });

    if (this.isEditMode && this.professor) {
      // En édition, les affectations affichées doivent être celles du
      // professeur : la liste est renvoyée telle quelle à l'enregistrement,
      // donc repartir du filtre écraserait ses matières.
      // Surtout pas de matière pré-ajoutée depuis le filtre en édition.
      this.pendingDefaultSubjectId = null;
      this.loadExistingAssignments(this.professor.id);
      if (this.learningPathId) this.loadSubjects(this.learningPathId);
      return;
    }

    if (this.learningPathId) {
      this.pendingDefaultSubjectId = this.subjectId;
      this.loadSubjects(this.learningPathId);
    }
  }

  /** Charge les affectations déjà enregistrées pour ce professeur. */
  private loadExistingAssignments(professorId: string): void {
    this.isLoadingAssignments = true;
    this.professorService
      .getAllAssignments()
      .pipe(finalize(() => (this.isLoadingAssignments = false)))
      .subscribe({
        next: (res) => {
          // Une réouverture rapide du modal peut avoir changé de professeur
          // entre-temps : on ignore une réponse qui ne le concerne plus.
          if (!this.isEditMode || this.professor?.id !== professorId) return;

          this.assignments = (res?.data ?? [])
            .filter((a) => String(a.professorId) === String(professorId))
            .map((a) => ({
              pathId: Number(a.learningPathId),
              pathTitle: a.learningPathTitle ?? '',
              subjectId: Number(a.subjectId),
              subjectName: a.subjectName ?? '',
            }));
        },
        error: () => {
          // Sans affectations fiables, mieux vaut bloquer l'enregistrement que
          // de renvoyer une liste vide qui effacerait celles du professeur.
          this.assignments = [];
          this.assignmentsLoadFailed = true;
        },
      });
  }

  /* ── Sélecteur d'affectation ─────────────────────────────── */

  onPathChange(): void {
    const pid = Number(this.professorForm.get('learningPathId')?.value) || null;
    this.professorForm.patchValue({ subjectId: null });
    this.subjects = [];
    this.rebuildSubjectOptions();
    if (pid) this.loadSubjects(pid);
  }

  private loadSubjects(pathId: number): void {
    this.learning.getSubjectsByLearningPath(pathId, false).subscribe({
      next: (res) => {
        this.subjects = res?.data ?? [];
        this.rebuildSubjectOptions();
        if (this.pendingDefaultSubjectId != null) {
          this.addAssignmentById(pathId, this.pendingDefaultSubjectId);
          this.pendingDefaultSubjectId = null;
        }
      },
      error: () => {
        this.subjects = [];
        this.rebuildSubjectOptions();
      },
    });
  }

  addAssignment(): void {
    const pid = Number(this.professorForm.get('learningPathId')?.value) || null;
    const sid = Number(this.professorForm.get('subjectId')?.value) || null;
    if (!pid || !sid) return;
    this.addAssignmentById(pid, sid);
    // On garde le parcours pour enchaîner plusieurs matières du même niveau.
    this.professorForm.patchValue({ subjectId: null });
  }

  private addAssignmentById(pathId: number, subjectId: number): void {
    if (this.assignments.some((a) => a.subjectId === subjectId)) return;
    const pathTitle =
      this.learningPaths.find((p) => p.id === pathId)?.title ?? '';
    const subjectName =
      this.subjects.find((s) => s.id === subjectId)?.name ?? '';
    this.assignments.push({ pathId, pathTitle, subjectId, subjectName });
    this.assignmentError = false;
  }

  removeAssignment(subjectId: number): void {
    this.assignments = this.assignments.filter(
      (a) => a.subjectId !== subjectId,
    );
  }

  /* ── Soumission ──────────────────────────────────────────── */

  onSubmit(): void {
    if (this.professorForm.invalid) {
      this.professorForm.markAllAsTouched();
      return;
    }
    // Le back remplace les affectations par la liste envoyée : tant qu'elle
    // n'est pas fiable, enregistrer effacerait les matières du professeur.
    if (this.isLoadingAssignments || this.assignmentsLoadFailed) return;
    if (this.assignments.length === 0) {
      this.assignmentError = true;
      return;
    }

    const v = this.professorForm.value;
    // Le back attend une liste plate d'IDs de matières (`subjectIds`), tous
    // parcours confondus : chaque matière porte déjà son parcours.
    const subjectIds = this.assignments.map((a) => a.subjectId);

    if (this.isEditMode && this.professor) {
      const updateData: UpdateProfessorDto = {
        id: Number(this.professor.id),
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        grade: v.grade,
        specialization: v.specialization,
        isActive: Boolean(v.isActive),
        imageKey: this.imageKey,
        subjectIds,
      };
      this.saved.emit(updateData);
    } else {
      const createData: CreateProfessorDto = {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        grade: v.grade,
        specialization: v.specialization,
        isActive: Boolean(v.isActive),
        imageKey: this.imageKey,
        subjectIds,
      };
      this.saved.emit(createData);
    }
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.isUploadingImage = true;
    this.uploadService.upload(file, UploadPurpose.ProfessorImage).subscribe({
      next: (result) => {
        this.imageKey = result.key;
        this.imagePreviewUrl = result.previewUrl;
        this.isUploadingImage = false;
      },
      error: () => {
        this.isUploadingImage = false;
      },
    });
  }

  removeImage(): void {
    this.imageKey = null;
    this.imagePreviewUrl = null;
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  /* ── Getters d'aide au gabarit ───────────────────────────── */

  get firstName() {
    return this.professorForm.get('firstName');
  }
  get lastName() {
    return this.professorForm.get('lastName');
  }
  get email() {
    return this.professorForm.get('email');
  }
  get grade() {
    return this.professorForm.get('grade');
  }
  get specialization() {
    return this.professorForm.get('specialization');
  }
  get isActive() {
    return this.professorForm.get('isActive');
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.professorForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.professorForm.get(fieldName);
    if (!field || !field.errors) return '';
    if (field.errors['required']) {
      return this.translate.instant('VALIDATION.REQUIRED');
    }
    if (field.errors['minlength']) {
      return this.translate.instant('VALIDATION.MIN_LENGTH', {
        min: field.errors['minlength'].requiredLength,
      });
    }
    if (field.errors['email']) {
      return this.translate.instant('VALIDATION.EMAIL_INVALID');
    }
    return '';
  }
}
